import { v4 as uuidv4 } from 'uuid'
import { EventEmitter } from 'events'
import Messenger from './Messenger'
import Messages from './Messages'
import { logger } from './logger'

export const DEFAULT_MESSAGE_TIMEOUT = 60 * 1000

/**
 *
 *
 * @class AbstractClient
 * @classdesc This is to create a client
 *
 */
class AbstractClient extends EventEmitter {
  get clientWindow() {
    return this.messenger.inputWindow
  }

  get hostWindow() {
    return this.messenger.outputWindow
  }

  set dataRequestHandler(value) {
    if (typeof value === 'function') {
      this.onDataRequestHandler = value
    }
  }

  set commandRequestHandler(value) {
    if (typeof value === 'function') {
      this.onCommandRequestHandler = value
    }
  }

  set dataTransferHandler(value) {
    if (typeof value === 'function') {
      this.onDataTransferHandler = value
    }
  }

  /**
   * Constructor of Client
   * @constructor
   * @param clientWindow
   * @param hostWindow
   * @param name
   * @param debug
   */
  constructor(clientWindow, hostWindow, name, debug) {
    super()

    this.name = name || `no-name-${uuidv4()}`
    this.debug = debug || false
    this.messenger = new Messenger(clientWindow, hostWindow, this.name, this.debug)
    this.promisedMessages = new Map()
    this.messageTimeouts = new Map()
    this.connected = false

    this.bindMessengerListeners()

    if (this.constructor === AbstractClient) {
      throw new Error(`You can not instantiate ${AbstractClient.name} directly, you need to create a sub class and instantiate this one instead.`)
    }
  }

  bindMessengerListeners() {
    this.onDataRequest = this.onDataRequest.bind(this)
    this.onDataResponse = this.onDataResponse.bind(this)
    this.onCommandRequest = this.onCommandRequest.bind(this)
    this.onCommandResponse = this.onCommandResponse.bind(this)
    this.onDataTransfer = this.onDataTransfer.bind(this)
  }

  addMessengerListeners() {
    this.messenger.addListener(Messenger.ON_DATA_REQUEST, this.onDataRequest)
    this.messenger.addListener(Messenger.ON_DATA_RESPONSE, this.onDataResponse)
    this.messenger.addListener(Messenger.ON_COMMAND_REQUEST, this.onCommandRequest)
    this.messenger.addListener(Messenger.ON_COMMAND_RESPONSE, this.onCommandResponse)
    this.messenger.addListener(Messenger.ON_DATA_TRANSFER, this.onDataTransfer)
  }

  removeMessengerListeners() {
    this.messenger.removeListener(Messenger.ON_DATA_REQUEST, this.onDataRequest)
    this.messenger.removeListener(Messenger.ON_DATA_RESPONSE, this.onDataResponse)
    this.messenger.removeListener(Messenger.ON_COMMAND_REQUEST, this.onCommandRequest)
    this.messenger.removeListener(Messenger.ON_COMMAND_RESPONSE, this.onCommandResponse)
    this.messenger.removeListener(Messenger.ON_DATA_TRANSFER, this.onDataTransfer)
  }

  /**
   *
   * @param correlationId
   * @param payload
   */
  onDataRequest({ correlationId, payload }) {
    if (this.onDataRequestHandler) {
      this.onDataRequestHandler({ correlationId, payload }, this.name)
    } else {
      this.onNonImplementedMethod('onDataRequest', 'dataRequestHandler')
    }
  }

  /**
   *
   * @param correlationId
   * @param payload
   */
  onCommandRequest({ correlationId, payload }) {
    if (this.onCommandRequestHandler) {
      this.onCommandRequestHandler({ correlationId, payload }, this.name)
    } else {
      this.onNonImplementedMethod('onCommandRequest', 'commandRequestHandler')
    }
  }

  /**
   *
   * @param payload
   */
  onDataTransfer({ payload }) {
    if (this.onDataTransferHandler) {
      this.onDataTransferHandler({ payload }, this.name)
    } else {
      this.onNonImplementedMethod('onDataTransfer', 'dataTransferHandler')
    }
  }

  /**
   *
   * @param methodName string
   * @param handlerName string
   */
  onNonImplementedMethod(methodName, handlerName) {
    if (this.debug) {
      logger.warn(`The method ${methodName} should be implemented in ${this.constructor.name}, or set the handler ${handlerName}`)
    }
  }

  /**
   *
   * @param resolve function
   * @param reject function
   * @param rejectAfter number
   * @return correlationId string
   */
  storePromisedMessage(resolve, reject, rejectAfter = DEFAULT_MESSAGE_TIMEOUT) {
    const correlationId = uuidv4()

    this.promisedMessages.set(correlationId, { resolve, reject })

    this.storeMessageTimeout(correlationId, rejectAfter)

    return correlationId
  }

  /**
   * @param correlationId
   * @param rejectAfter
   */
  storeMessageTimeout(correlationId, rejectAfter) {
    if (typeof rejectAfter === 'number' && rejectAfter > -1) {
      const messageTimeout = window.setTimeout(() => {
        this.clearPromisedMessage(
          correlationId,
          null,
          new Error('Message timed out')
        )
      }, rejectAfter)

      this.messageTimeouts.set(correlationId, messageTimeout)
    }
  }

  /**
   *
   * @param correlationId
   * @param payload
   * @param error
   */
  clearPromisedMessage(correlationId, payload, error) {
    if (this.hasPromiseMessage(correlationId)) {
      if (error) {
        this.promisedMessages.get(correlationId).reject(error)
      } else {
        this.promisedMessages.get(correlationId).resolve(payload)
      }
      this.promisedMessages.delete(correlationId)
    }

    this.clearMessageTimeout(correlationId)
  }

  /**
   *
   * @param correlationId
   */
  clearMessageTimeout(correlationId) {
    if (this.messageTimeouts.has(correlationId)) {
      window.clearTimeout(this.messageTimeouts.get(correlationId))
      this.messageTimeouts.delete(correlationId)
    }
  }

  /**
   *
   * @param correlationId
   * @return {boolean}
   */
  hasPromiseMessage(correlationId) {
    return correlationId ? this.promisedMessages.has(correlationId) : false
  }

  /**
   *
   * @param correlationId
   * @param payload
   * @param error
   */
  onDataResponse({ correlationId, payload }, error) {
    this.clearPromisedMessage(correlationId, payload, error)
  }

  /**
   *
   * @param correlationId
   * @param payload
   * @param error
   */
  onCommandResponse({ correlationId, payload }, error) {
    this.clearPromisedMessage(correlationId, payload, error)
  }

  /**
   *
   * @param resourceName
   * @return {Promise<any>}
   */
  sendDataRequest(resourceName) {
    return new Promise((resolve, reject) => {
      const correlationId = this.storePromisedMessage(resolve, reject)
      this.messenger.sendMessage({
        type: Messages.DATA_REQUEST,
        correlationId,
        payload: { name: resourceName }
      })
    })
  }

  /**
   *
   * @param resource
   * @param correlationId
   * @param error
   */
  sendDataResponse(resource, correlationId, error) {
    this.messenger.sendMessage({
      type: Messages.DATA_RESPONSE,
      correlationId,
      error,
      payload: { data: resource }
    })
  }

  /**
   *
   * @param commandName
   * @param commandOptions
   * @return {Promise<any>}
   */
  sendCommandRequest(commandName, commandOptions) {
    return new Promise((resolve, reject) => {
      const correlationId = this.storePromisedMessage(resolve, reject)
      this.messenger.sendMessage({
        type: Messages.COMMAND_REQUEST,
        correlationId,
        payload: { name: commandName, options: commandOptions }
      })
    })
  }

  /**
   *
   * @param commandResult
   * @param correlationId
   * @param error
   */
  sendCommandResponse(commandResult, correlationId, error) {
    this.messenger.sendMessage({
      type: Messages.COMMAND_RESPONSE,
      correlationId,
      error,
      payload: { result: commandResult }
    })
  }

  /**
   *
   * @param data
   */
  sendDataTransfer(data) {
    this.messenger.sendMessage({
      type: Messages.DATA_TRANSFER,
      payload: { data }
    })
  }
}


export default AbstractClient
